import React, { useState, useEffect } from "react";
import axiosInstance from '../axiosConfig';
import Header from "../Components/Header";
import Sidebar from "../Components/Sidebar";
import '../styles.css';

const LeaveRequest = () => {
  const [userData, setUserData] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [numDays, setNumDays] = useState(0);
  const [reason, setReason] = useState('');
  const [leaveDuration, setLeaveDuration] = useState('full_day'); 
  const [isPlanned, setIsPlanned] = useState(false);
  const [isUnplanned, setIsUnplanned] = useState(false);
  const [approver, setApprover] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const currentUserId = localStorage.getItem('id'); 
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 520) {
      setIsSidebarVisible(false);
    } else {
      setIsSidebarVisible(true);
    }
  }, []);

  const handleToggleClick = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  useEffect(() => {
    if (currentUserId) {
      axiosInstance.get(`/user/${currentUserId}/`)
        .then((response) => setUserData(response.data))
        .catch((error) => console.error('Error fetching user data:', error));
    }
  }, [currentUserId]);

  useEffect(() => {
    axiosInstance.get('/leaves/')
      .then((response) => setLeaveTypes(response.data))
      .catch((error) => console.error('Error fetching leave types:', error));
  }, []);

  useEffect(() => {
    axiosInstance.get(`/assign-manager/${currentUserId}/`)
      .then((response) => setApprover(response.data))
      .catch((error) => console.error('Error fetching approver data:', error));
  }, [currentUserId]);

  useEffect(() => {
    if (userData && userData.isAdmin) {
      axiosInstance.get('/admin/')
        .then((response) => setApprover(response.data))
        .catch((error) => console.error('Error fetching approver data:', error));
    }
  }, [userData]);

  useEffect(() => {
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const days = Math.round((to - from) / (1000 * 3600 * 24)) + 1;
      setNumDays(days > 0 ? days : 0);
    }
  }, [fromDate, toDate]);

  const handleLeaveTypeChange = (e) => {
    setSelectedLeaveType(e.target.value);
    if (error) setError(null);
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    if (error) setError(null);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    if (error) setError(null);
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
    if (error) setError(null);
  };

  const handleLeaveDurationChange = (e) => {
    setLeaveDuration(e.target.value);
  };

  const handleIsPlannedChange = (e) => {
    setIsPlanned(e.target.checked);
    if (e.target.checked) setIsUnplanned(false);
  };

  const handleIsUnplannedChange = (e) => {
    setIsUnplanned(e.target.checked);
    if (e.target.checked) setIsPlanned(false);
  };

  const handleApplyLeave = () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const selectedLeave = leaveTypes.find((leave) => leave.name === selectedLeaveType);

    // Front-end computed days are date-range days.
    // Convert to “effective leave days” based on duration.
    const effectiveDays = leaveDuration === 'half_day' ? numDays * 0.5 : numDays;

    if (selectedLeave && effectiveDays > selectedLeave.days_allowed) {
      setError('You cannot apply for leave more than the days allowed');
      setIsLoading(false);
      return;
    }

    const leaveData = {
      leave_type: selectedLeave?.id,
      start_date: fromDate,
      end_date: toDate,
      reason: reason,
      leave_duration: leaveDuration,
    };


    axiosInstance.post('/leave-requests/submit/', leaveData)
      .then((response) => {
        console.log('Leave applied successfully!');
        setSuccess('Leave applied successfully!');
        setSelectedLeaveType('');
        setFromDate('');
        setToDate('');
        setNumDays(0);
        setReason('');
        setLeaveDuration('full_day');
        setIsPlanned(false);
        setIsUnplanned(false);
      })
      .catch((error) => {
        console.error('Error applying leave:', error);

        const serverErrors = error?.response?.data?.non_field_errors;
        if (Array.isArray(serverErrors) && serverErrors.length > 0) {
          // Expected format:
          // { "non_field_errors": ["Insufficient balance for Earned Leave..."] }
          setError(serverErrors[0]);
        } else {
          setError('Error applying leave. Please try again.');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const profilePhotoUrl = userData?.profile_photo?.startsWith('http://') 
    ? userData?.profile_photo.replace('http://', 'https://') 
    : userData?.profile_photo;

  const menuItems = [
    { icon: '🏠', label: 'Dashboard', path: '/user-dashboard' },
    { icon: '👤', label: 'My Profile', path: `/user-profile/${userData?.id}` },
    { icon: '📅', label: 'Attendance', path: `/leave-attendance/${userData?.id}` },
    { icon: '📋', label: 'Leave Management', path: `/leave-management/${userData?.id}` },
    { icon: '✏️', label: 'Create Leave Request', path: `/leave-request/${userData?.id}` },
    { icon: '💰', label: 'Leave Balance', path: '/leave-balance' },
    { icon: '📊', label: 'Leave Transactions', path: '/leave-transaction/' },
    // { icon: '📅', label: 'Calendar', path: '/calendar' },
    // { icon: '&#128178;', label: 'Payroll', path: 'https://hrms.teamhalt.com/', active: false },
    { icon: '🆔', label: 'ID Card', action: () => setIsIdCardPopupVisible(true) },
  ];

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header 
        onToggleClick={handleToggleClick} 
        isSidebarVisible={isSidebarVisible} 
      />
      
      <Sidebar 
        isSidebarVisible={isSidebarVisible}
        currentUser={userData}
        menuItems={menuItems}
        handleLogout={handleLogout}
      />
      
      <div className="dashboard-main-content">
        <div className="leave-request-container">
          <div className="leave-request-header">
            <h1>Leave Request</h1>
            <p>Submit a new leave request for approval</p>
          </div>

          <div className="leave-request-card">
            {error && (
              <div className="leave-request-error">
                <span className="error-icon">⚠</span>
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="leave-request-success">
                <span className="success-icon">✓</span>
                <span>{success}</span>
              </div>
            )}

            <div className="leave-request-form">
              <div className="leave-form-section">
                <h3>Leave Details</h3>
                
                <div className="form-group">
                  <label htmlFor="leaveType">Leave Type</label>
                  <select 
                    id="leaveType"
                    value={selectedLeaveType} 
                    onChange={handleLeaveTypeChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select Leave Type</option>
                    {leaveTypes.map((leaveType) => (
                      <option key={leaveType.id} value={leaveType.name}>
                        {leaveType.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="date-range-grid">
                  <div className="form-group">
                    <label htmlFor="fromDate">From Date</label>
                    <input 
                      type="date" 
                      id="fromDate"
                      value={fromDate} 
                      onChange={handleFromDateChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="toDate">To Date</label>
                    <input 
                      type="date" 
                      id="toDate"
                      value={toDate} 
                      onChange={handleToDateChange}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="numDays">Number of Days</label>
                  <input 
                    type="number" 
                    id="numDays"
                    value={numDays} 
                    disabled 
                    className="form-input disabled"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="reason">Reason</label>
                  <textarea 
                    id="reason"
                    value={reason} 
                    onChange={handleReasonChange}
                    className="form-textarea"
                    placeholder="Enter your reason for leave"
                    rows="4"
                    required
                  />
                </div>

              </div>

              <div className="leave-form-section">
                <h3>Additional Details</h3>
                
                <div className="form-group">
                  <label htmlFor="leaveDuration">Leave Duration</label>
                  <select 
                    id="leaveDuration"
                    value={leaveDuration} 
                    onChange={handleLeaveDurationChange}
                    className="form-select"
                  >
                    <option value="full_day">Full Day</option>
                    <option value="half_day">Half Day</option>
                  </select>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={isPlanned} 
                      onChange={handleIsPlannedChange}
                    />
                    <span className="checkbox-custom"></span>
                    Planned Leave
                  </label>
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={isUnplanned} 
                      onChange={handleIsUnplannedChange}
                    />
                    <span className="checkbox-custom"></span>
                    Unplanned Leave
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="approver">Approver</label>
                  <input 
                    type="text" 
                    id="approver"
                    value={approver.manager_name || 'N/A'} 
                    disabled 
                    className="form-input disabled"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="submit-leave-btn"
                  onClick={handleApplyLeave}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    'Apply Leave'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {isIdCardPopupVisible && (
        <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
          <div className="id-card-popup" onClick={(e) => e.stopPropagation()}>
            <button className="id-card-close" onClick={() => setIsIdCardPopupVisible(false)}>×</button>
            
            <div className="id-card-header">
              <img src="/Teamhalt.png" alt="Logo" className="id-card-logo" />
              <span>TEAMHALT PRIVATE LIMITED</span>
            </div>
            
            <div className="id-card-body">
                <div className="id-card-profile">
                <img
                  src={profilePhotoUrl || '/profile.jpg'}
                  alt="Profile"
                  className="id-card-photo"
                />
                <div className="id-card-info">
                  <h4>{userData?.first_name} {userData?.last_name}</h4>
                  <p className="id-designation">{userData?.designation}</p>
                  <div className="id-dob">
                    <strong>DOB:</strong>
                    <span>
                      {userData?.date_of_birth
                        ? new Date(userData.date_of_birth).toLocaleDateString("en-GB").replace(/\//g, "-")
                        : "N/A"}
                    </span>
                  </div>
                </div>
                </div>
              
              <hr className="id-card-divider" />
              
              <div className="id-card-details">
                {[
                  { label: "Emp ID", value: userData?.employee_code },
                  { label: "Department", value: userData?.department },
                  { label: "ESIC No.", value: userData?.esic_no },
                  { label: "UAN No.", value: userData?.uan_no },
                  { label: "Gender", value: userData?.gender },
                  { label: "Contact No.", value: userData?.contact_number },
                ].map((item, idx) => (
                  <div key={idx} className="detail-row">
                    <strong>{item.label}:</strong>
                    <span>{item.value || "N/A"}</span>
                  </div>
                ))}
              </div>
              
              {typeof userData?.work_location_name === "string" &&
                userData.work_location_name.toLowerCase() !== "noida" && (
                <div className="id-card-duty">
                  <strong>On Contractual Duty Of:</strong>
                  <span>{userData?.duty_of || "N/A"}</span>
                </div>
              )}
              
              <div className="id-card-footer">
                https://teamhalt.com/
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRequest;
